import React, { useEffect, useState } from 'react';
import { Col, Dropdown, Modal, Row, Spin, message } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import {
  AppointmentStatusSelect,
  ThemeButton,
  ThemeInput,
} from '../components';
import {
  formatToEastern,
  formatTimeET,
  formatDateET,
  formatDateTimeET,
  isInvoicable,
  sortDogsByName,
} from '../../helpers/helpers';
import {
  BOARDING_SERVICE_ID,
  APPOINTMENT_STATUS,
} from '../../constants/constants';
import DogByIDModal from '../dogByIDModal/dogByIDModal';
import EditDogModal from '../editDogModal/editDogModal';
import { useGetDogByIDQuery } from '../../services/dogsApi';
import { useSendInvoiceMutation } from '../../services/appointments';

const UpdateInvoiceModal = ({
  isOpen,
  onClose,
  data,
  setData,
  onSubmit,
  isLoading,
  onInvoiceSent,
  onModify,
  onViewHistory,
  onStatusChange,
  isStatusUpdating,
  zIndex,
}) => {
  const [noOfDogs, setNoOfDogs] = useState(data?.dogs_details?.length);
  const [noOfDays, setNoOfDays] = useState(0);
  const [chargePerDay, setChargesPerDay] = useState(0);
  const [tax, setTax] = useState(data?.tax);
  const [discount, setDiscount] = useState(data?.discount);
  const [extraCharges, setExtraCharges] = useState(data?.extraCharges || 0);
  const [latePickupFee, setLatePickupFee] = useState(data?.latePickupFee || 0);
  const [selectedDogId, setSelectedDogId] = useState(null);
  const [isDogModalOpen, setIsDogModalOpen] = useState(false);
  const [isEditDogModalOpen, setIsEditDogModalOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const { data: selectedDogData, isLoading: isDogLoading } = useGetDogByIDQuery(
    selectedDogId,
    {
      skip: !selectedDogId,
    }
  );

  const [sendInvoice, { isLoading: isSendingInvoice }] =
    useSendInvoiceMutation();

  // Check if this is a boarding service (overnight)
  const isBoardingService = data?.service?._id === BOARDING_SERVICE_ID;

  // Check if appointment is cancelled
  const isCancelled = data?.status === APPOINTMENT_STATUS.CANCELLED;

  // Check if appointment is paid
  const isPaid = data?.isPaid === true;

  // Helper function to format status display
  const formatStatus = status => {
    if (!status) return '';
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  useEffect(() => {
    // Use backend priceBreakdown.numberOfDays as the source of truth
    const calculatedDays =
      data?.priceBreakdown?.numberOfDays ??
      (isBoardingService ? 0 : data?.appointment_dates?.length || 0);
    setNoOfDays(calculatedDays);

    // Use tax rate stored with the appointment
    setTax(data?.tax || 0);

    setNoOfDogs(data?.dogs_details?.length);
    setDiscount(data?.discount);
    setExtraCharges(data?.extraCharges || 0);
    setLatePickupFee(data?.latePickupFee || 0);
    setChargesPerDay(data?.chargePerDay);
    setIsDirty(false);
    // Keyed on the appointment id, not the `data` object: a status change from
    // the dropdown below hands us a new object for the same appointment, and
    // re-seeding there would silently discard unsaved Discount / Extra Charges.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?._id, isBoardingService]);

  let baseAmount =
    Number(noOfDays || 0) * Number(chargePerDay || 0) * Number(noOfDogs || 1);
  let subtotal =
    Number(baseAmount) +
    Number(extraCharges) +
    Number(latePickupFee) -
    Number(discount);
  let taxPrice = (
    Math.round(subtotal * ((Number(tax) || 0) / 100) * 100) / 100
  ).toFixed(2);
  let total = (Number(subtotal) + Number(taxPrice)).toFixed(2);

  const onUpdateInvoice = async () => {
    try {
      let obj = {
        discount: discount,
        extraCharges: extraCharges,
        netTotal: parseFloat(total),
      };
      await onSubmit(obj);
      message.success('Invoice updated successfully!');
      setIsDirty(false);
    } catch (error) {
      console.error('Error updating invoice:', error);
      message.error('Failed to update invoice. Please try again.');
    }
  };

  const handleDogClick = dogId => {
    setSelectedDogId(dogId);
    setIsDogModalOpen(true);
  };

  const handleCloseDogModal = () => {
    setIsDogModalOpen(false);
    setSelectedDogId(null);
  };

  const handleEditDog = () => {
    setIsDogModalOpen(false);
    setIsEditDogModalOpen(true);
  };

  const handleCloseEditDogModal = () => {
    setIsEditDogModalOpen(false);
    setSelectedDogId(null);
  };

  const handleSendInvoice = async () => {
    // Check if invoice was already sent
    if (data?.invoice_sent_at) {
      const customerName = data?.user?.username || 'Unknown Customer';
      const serviceTitle = data?.service?.title || 'N/A';
      const lastSentDate = formatDateTimeET(data.invoice_sent_at);

      Modal.confirm({
        title: 'Re-send Invoice Confirmation',
        content: (
          <div>
            <p>
              This invoice has already been sent to the customer. Are you sure
              you want to re-send it?
            </p>
            <div
              style={{
                marginTop: '12px',
                padding: '8px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
              }}
            >
              <p>
                <strong>Customer:</strong> {customerName}
              </p>
              <p>
                <strong>Service:</strong> {serviceTitle}
              </p>
              <p>
                <strong>Last Sent:</strong> {lastSentDate}
              </p>
              <p>
                <strong>Amount:</strong> ${total}
              </p>
            </div>
          </div>
        ),
        okText: 'Yes, Re-send',
        cancelText: 'Cancel',
        zIndex: 2000,
        okButtonProps: {
          style: {
            backgroundColor: '#10b981',
            borderColor: '#10b981',
            color: '#ffffff',
          },
        },
        cancelButtonProps: {
          style: {
            backgroundColor: '#ffffff',
            borderColor: '#d1d5db',
            color: '#374151',
          },
        },
        onOk: async () => {
          try {
            await sendInvoice({ id: data?._id }).unwrap();
            message.success('Invoice re-sent successfully!');
            if (onInvoiceSent) {
              onInvoiceSent();
            }
            onClose();
          } catch (error) {
            console.error('Error sending invoice:', error);
            message.error('Failed to send invoice. Please try again.');
          }
        },
      });
    } else {
      // Send invoice without confirmation if it hasn't been sent before
      try {
        await sendInvoice({ id: data?._id }).unwrap();
        message.success('Invoice sent successfully!');
        if (onInvoiceSent) {
          onInvoiceSent();
        }
        onClose();
      } catch (error) {
        console.error('Error sending invoice:', error);
        message.error('Failed to send invoice. Please try again.');
      }
    }
  };

  return (
    <Modal
      width={800}
      footer={false}
      open={isOpen}
      onCancel={onClose}
      centered
      zIndex={zIndex}
      styles={{
        body: { padding: '24px', position: 'relative', overflow: 'visible' },
      }}
    >
      {isPaid && (
        <div
          className="absolute"
          style={{
            top: '30%',
            right: '5%',
            fontSize: '8rem',
            fontWeight: '900',
            color: '#16a34a',
            opacity: 0.4,
            fontFamily: 'Arial, sans-serif',
            letterSpacing: '0.5em',
            userSelect: 'none',
            pointerEvents: 'none',
            zIndex: 1000,
            textShadow: `
              4px 4px 0px rgba(22, 163, 74, 0.7),
              -2px -2px 0px rgba(22, 163, 74, 0.7),
              2px -2px 0px rgba(22, 163, 74, 0.7),
              -2px 2px 0px rgba(22, 163, 74, 0.7),
              0 0 25px rgba(34, 197, 94, 0.5)
            `,
            filter: 'drop-shadow(4px 4px 8px rgba(0,0,0,0.2))',
            transform: 'rotate(12deg)',
          }}
        >
          PAID
        </div>
      )}
      {isPaid && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded relative z-10">
          <p className="text-sm text-green-800">
            This reservation is <span className="font-semibold">paid</span> and
            can no longer be modified. To make a change, uncheck{' '}
            <span className="font-semibold">Paid</span> on the appointments
            list, edit the reservation, then mark it paid again.
          </p>
        </div>
      )}
      <div className="mb-6 flex items-center gap-2">
        <h1 className="text-xl font-bold">Appointment</h1>
        {(onModify || onViewHistory) && data?._id && (
          <Dropdown
            trigger={['click']}
            placement="bottomLeft"
            menu={{
              items: [
                ...(onModify &&
                data?.status !== APPOINTMENT_STATUS.CANCELLED &&
                data?.status !== APPOINTMENT_STATUS.CHECKED_OUT
                  ? [
                      {
                        key: 'modify-appointment',
                        label: 'Modify Appointment',
                      },
                    ]
                  : []),
                ...(onViewHistory
                  ? [{ key: 'view-log', label: 'View Log' }]
                  : []),
              ],
              onClick: ({ key }) => {
                if (key === 'modify-appointment') onModify(data);
                else if (key === 'view-log') onViewHistory(data._id);
              },
            }}
          >
            <button
              type="button"
              className="text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full w-7 h-7 flex items-center justify-center transition-colors"
              aria-label="Appointment actions"
            >
              <MoreOutlined className="text-lg" />
            </button>
          </Dropdown>
        )}
      </div>

      <div className="mb-4 flex justify-between items-start">
        <div>
          <p className="text-primary font-semibold text-base">Service</p>
          <p>
            {data?.service?.title} {isBoardingService ? '🌙' : '☀️'}
          </p>
          <p className="text-xs text-gray-500 mt-1">Appointment: {data?._id}</p>
        </div>
        <div className="flex flex-col gap-4 text-right">
          <div>
            <p className="text-primary font-semibold text-base">Created</p>
            <p>{formatDateTimeET(data?.createdAt || data?.created_at)}</p>
          </div>
          <div>
            <p className="text-primary font-semibold text-base">Modified</p>
            <p>{formatDateTimeET(data?.updatedAt || data?.updated_at)}</p>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <p className="text-primary font-semibold text-base">Dogs</p>
        <div className="flex items-start gap-3 overflow-x-auto py-2">
          {noOfDogs > 0 &&
            sortDogsByName(data?.dogs_details).map((dog, index) => {
              if (!dog) return null;
              return (
                <div
                  className="flex flex-col items-center flex-shrink-0"
                  key={dog?._id || index}
                >
                  <img
                    className="rounded-full object-cover w-12 h-12 cursor-pointer hover:ring-2 hover:ring-blue-300 transition-all"
                    src={dog?.avatar || '/default-dog-avatar.png'}
                    alt={dog?.dog_name || `Dog ${index + 1}`}
                    onClick={() => dog?._id && handleDogClick(dog._id)}
                    title={`Click to view ${dog?.dog_name || 'dog'} details`}
                  />
                  <p className="text-xs text-gray-600 mt-1 text-center max-w-[60px] truncate">
                    {dog?.dog_name || `Dog ${index + 1}`}
                  </p>
                </div>
              );
            })}
        </div>
      </div>

      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">
          Appointment Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {isBoardingService ? (
            <>
              <div className="mb-3 pb-3">
                <p className="text-sm font-medium text-gray-600">
                  Appointment ID:
                </p>
                <p className="text-base font-mono text-gray-800">{data?._id}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Drop-off:</p>
                <p className="text-base font-semibold">
                  {formatToEastern(
                    data?.appointment_start_time,
                    'dddd, MMMM D, YYYY [at] h:mm A'
                  )}{' '}
                  ET
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Pick-up:</p>
                <p className="text-base font-semibold">
                  {formatToEastern(
                    data?.appointment_end_time,
                    'dddd, MMMM D, YYYY [at] h:mm A'
                  )}{' '}
                  ET
                </p>
              </div>
              <div className="md:col-span-2">
                <p className="text-sm font-medium text-gray-600 mb-2">
                  Service Dates:
                </p>
                <div className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                  {formatDateET(data?.appointment_start_time, 'ddd MMM D')} -{' '}
                  {formatDateET(data?.appointment_end_time, 'ddd MMM D')}
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Duration:</p>
                <p className="text-base font-semibold">
                  {noOfDays} {noOfDays === 1 ? 'night' : 'nights'}
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="md:col-span-2">
                <p className="text-sm font-medium text-gray-600 mb-2">
                  Service Dates:
                </p>
                <div className="flex flex-wrap gap-2">
                  {data?.appointment_dates &&
                  data.appointment_dates.length > 0 ? (
                    data.appointment_dates.map((date, index) => (
                      <div
                        key={index}
                        className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                      >
                        {formatDateET(date, 'ddd MMM D')}
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">No dates selected</p>
                  )}
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Daily Check-in:
                </p>
                <p className="text-base font-semibold">
                  {formatTimeET(data?.appointment_start_time)}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Daily Check-out:
                </p>
                <p className="text-base font-semibold">
                  {formatTimeET(data?.appointment_end_time)}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Duration:</p>
                <p className="text-base font-semibold">
                  {noOfDays} {noOfDays === 1 ? 'day' : 'days'}
                </p>
              </div>
            </>
          )}
          {onStatusChange ? (
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-gray-600">Status:</p>
              <AppointmentStatusSelect
                status={data?.status}
                onChange={newStatus => onStatusChange(newStatus, data)}
                disabled={isStatusUpdating}
                loading={isStatusUpdating}
                className="w-40"
              />
            </div>
          ) : (
            <div>
              <p className="text-sm font-medium text-gray-600">Status:</p>
              <p className="text-base font-semibold">
                {formatStatus(data?.status)}
              </p>
            </div>
          )}
        </div>
        {data?.notes && (
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-600">Notes:</p>
            <p className="text-base">{data?.notes}</p>
          </div>
        )}
      </div>

      <Row gutter={{ xs: 8, sm: 16, md: 20, lg: 20 }}>
        <Col xl={6} lg={6} md={6} sm={12} xs={12} className="mb-4">
          <p className="text-primary font-semibold text-base"># of Dogs</p>
          <ThemeInput
            type={'number'}
            placeholder={0}
            value={noOfDogs}
            onChange={e => setNoOfDogs(e.target.value)}
            readOnly={true}
            disabled={true}
          />
        </Col>
        <Col xl={6} lg={6} md={6} sm={12} xs={12} className="mb-4">
          <p className="text-primary font-semibold text-base">
            {isBoardingService ? '🌙 # of Nights' : '☀️ # of Days'}
          </p>
          <ThemeInput
            type={'number'}
            placeholder={0}
            value={noOfDays}
            onChange={e => setNoOfDays(e.target.value)}
            readOnly={true}
            disabled={true}
          />
        </Col>
        <Col xl={6} lg={6} md={6} sm={12} xs={12} className="mb-4">
          <p className="text-primary font-semibold text-base">
            {isBoardingService ? 'Charge Per Night' : 'Charge Per Day'}
          </p>
          <ThemeInput
            type={'number'}
            placeholder={0}
            value={chargePerDay}
            onChange={e => setChargesPerDay(e.target.value)}
            readOnly={true}
            disabled={true}
          />
        </Col>
        <Col xl={6} lg={6} md={6} sm={12} xs={12} className="mb-4">
          <p className="text-primary font-semibold text-base">Extra Charges</p>
          <ThemeInput
            type={'number'}
            placeholder={0}
            value={extraCharges}
            onChange={e => {
              setExtraCharges(Math.max(0, e.target.value));
              setIsDirty(true);
            }}
            min={0}
            disabled={!isInvoicable(data?.status) || isPaid}
          />
        </Col>
        <Col xl={6} lg={6} md={6} sm={12} xs={12} className="mb-4">
          <p className="text-primary font-semibold text-base">Discount ($)</p>
          <ThemeInput
            type={'number'}
            placeholder={0}
            value={discount}
            onChange={e => {
              const maxDiscount =
                Number(baseAmount) +
                Number(extraCharges) +
                Number(latePickupFee);
              setDiscount(Math.max(0, Math.min(maxDiscount, e.target.value)));
              setIsDirty(true);
            }}
            min={0}
            max={
              Number(baseAmount) + Number(extraCharges) + Number(latePickupFee)
            }
            disabled={!isInvoicable(data?.status) || isPaid}
          />
        </Col>
        <Col xl={6} lg={6} md={6} sm={12} xs={12} className="mb-4">
          <p className="text-primary font-semibold text-base">Tax Rate</p>
          <ThemeInput
            type={'number'}
            placeholder={0}
            value={tax}
            onChange={e => setTax(e.target.value)}
            readOnly={true}
            disabled={true}
          />
        </Col>
      </Row>

      <div className="flex justify-end mt-4 mb-6 sm:pr-10 pr-4">
        <div className="w-full sm:w-80 text-sm">
          <div className="flex justify-between py-1">
            <span className="font-semibold text-gray-700">Base Amount</span>
            <span className="font-semibold text-gray-900 tabular-nums">
              ${baseAmount.toFixed(2)}
            </span>
          </div>
          {Number(latePickupFee || 0) > 0 && (
            <div className="flex justify-between py-1">
              <span className="font-semibold text-gray-700">
                Afternoon Pickup
              </span>
              <span className="font-semibold text-gray-900 tabular-nums">
                ${Number(latePickupFee || 0).toFixed(2)}
              </span>
            </div>
          )}
          <div className="flex justify-between py-1">
            <span className="font-semibold text-gray-700">Extra Charges</span>
            <span className="font-semibold text-gray-900 tabular-nums">
              ${Number(extraCharges || 0).toFixed(2)}
            </span>
          </div>
          {Number(discount || 0) > 0 && (
            <div className="flex justify-between py-1 px-2 -mx-2 my-1 rounded bg-green-100">
              <span className="font-semibold text-gray-700">Discount</span>
              <span className="font-semibold text-gray-900 tabular-nums">
                -${Number(discount || 0).toFixed(2)}
              </span>
            </div>
          )}
          <div className="border-t border-gray-200 mt-2 pt-2">
            <div className="flex justify-between py-1">
              <span className="font-semibold text-gray-700">Subtotal</span>
              <span className="font-semibold text-gray-900 tabular-nums">
                ${subtotal.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between py-1">
              <span className="font-semibold text-gray-700">
                Tax{' '}
                <span className="text-xs font-normal text-gray-500">
                  ({tax}%)
                </span>
              </span>
              <span className="font-semibold text-gray-900 tabular-nums">
                ${taxPrice}
              </span>
            </div>
          </div>
          <div className="border-t border-gray-300 mt-2 pt-2">
            <div className="flex justify-between items-baseline">
              <span className="text-base font-bold text-gray-900">Total</span>
              <span className="text-base font-bold text-gray-900 tabular-nums">
                ${total}
              </span>
            </div>
          </div>
        </div>
      </div>

      {data?.invoice_sent_at && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-800 font-semibold text-sm text-center">
            📧 Invoice Last Sent: {formatDateTimeET(data.invoice_sent_at)}
          </p>
        </div>
      )}

      <div className="flex items-center justify-end">
        <ThemeButton
          content={<>{isLoading ? <Spin /> : 'Update Invoice'}</>}
          className={'mx-2 w-32'}
          onClick={onUpdateInvoice}
          disabled={isLoading || !isInvoicable(data?.status) || isPaid}
        />
        <ThemeButton
          content={<>{isSendingInvoice ? <Spin /> : 'Send Invoice'}</>}
          className={'mx-2'}
          onClick={handleSendInvoice}
          disabled={isSendingInvoice || !isInvoicable(data?.status) || isDirty}
        />
      </div>

      {/* Dog Details Modal */}
      <DogByIDModal
        isOpen={isDogModalOpen}
        onClose={handleCloseDogModal}
        data={selectedDogData?.data}
        isLoading={isDogLoading}
        onEditDog={handleEditDog}
        zIndex={(zIndex || 1000) + 10}
      />

      {/* Edit Dog Modal */}
      <EditDogModal
        isOpen={isEditDogModalOpen}
        onClose={handleCloseEditDogModal}
        data={selectedDogData?.data}
        zIndex={(zIndex || 1000) + 10}
      />
    </Modal>
  );
};

export default UpdateInvoiceModal;
